<template>
	<!-- Campos de un establecimiento o de un punto de emisión. Los usan la tarjeta de
	establecimientos y el diálogo de la actividad, que también puede crear uno ahí mismo.
	Modos: 'establishment' (alta completa), 'point' (punto nuevo en un establecimiento) y
	'edit' (nombre y dirección de uno que ya existe). Los errores llegan con los nombres de
	campo del backend (`establishmentAddress`, `seq_receipt`…). -->
	<div class="emission-fields">
		<div v-if="mode !== 'edit'" class="formgrid grid">
			<div v-if="mode === 'establishment'" class="field col-12 sm:col-6">
				<label :for="fieldId('code')" class="field-label">Código del establecimiento</label>
				<InputText :id="fieldId('code')" :modelValue="modelValue.code" @update:modelValue="set('code', $event)"
					maxlength="3" inputmode="numeric" class="code-input" :class="{'p-invalid': errors.establishmentCode}"
					:aria-describedby="fieldId('code-help')" />
				<small v-if="errors.establishmentCode" :id="fieldId('code-help')" class="field-error" role="alert">{{ errors.establishmentCode }}</small>
				<small v-else :id="fieldId('code-help')" class="field-help">El de tu RUC. Sugerimos el siguiente libre.</small>
			</div>
			<div class="field col-12 sm:col-6">
				<label :for="fieldId('point')" class="field-label">{{ mode === 'point' ? 'Código del punto' : 'Primer punto de emisión' }}</label>
				<InputText :id="fieldId('point')" :modelValue="modelValue.pointCode" @update:modelValue="set('pointCode', $event)"
					maxlength="3" inputmode="numeric" class="code-input" :class="{'p-invalid': errors.emitionPointCode}"
					:aria-describedby="fieldId('point-help')" />
				<small v-if="errors.emitionPointCode" :id="fieldId('point-help')" class="field-error" role="alert">{{ errors.emitionPointCode }}</small>
				<small v-else :id="fieldId('point-help')" class="field-help">La serie será <span class="series">{{ series }}</span>.</small>
			</div>
		</div>

		<template v-if="mode !== 'point'">
			<div class="field">
				<label :for="fieldId('name')" class="field-label">Nombre</label>
				<InputText :id="fieldId('name')" :modelValue="modelValue.name" @update:modelValue="set('name', $event)"
					:maxlength="nameMaxLength" :placeholder="namePlaceholder" :class="{'p-invalid': errors.establishmentName}"
					:aria-describedby="fieldId('name-help')" />
				<small v-if="errors.establishmentName" :id="fieldId('name-help')" class="field-error" role="alert">{{ errors.establishmentName }}</small>
				<small v-else :id="fieldId('name-help')" class="field-help">Para reconocerlo en Contablus. No sale en el comprobante.</small>
			</div>
			<div class="field">
				<label :for="fieldId('address')" class="field-label">Dirección</label>
				<Textarea :id="fieldId('address')" :modelValue="modelValue.address" @update:modelValue="set('address', $event)"
					rows="2" :autoResize="true" :maxlength="addressMaxLength" placeholder="Ej.: Av. 4 de Noviembre y calle 113, Manta"
					:class="{'p-invalid': errors.establishmentAddress}" :aria-describedby="fieldId('address-help')" />
				<div class="address-foot">
					<small v-if="errors.establishmentAddress" :id="fieldId('address-help')" class="field-error" role="alert">{{ errors.establishmentAddress }}</small>
					<small v-else :id="fieldId('address-help')" class="field-help">Tal como figura en tu RUC. Sale en cada comprobante que emitas desde este establecimiento.</small>
					<small class="counter" aria-hidden="true">{{ (modelValue.address || '').length }}/{{ addressMaxLength }}</small>
				</div>
			</div>
		</template>

		<!-- Para quien ya facturaba en otro sistema: sigue la numeración y no repite comprobantes. -->
		<div v-if="mode !== 'edit'" class="seq-block">
			<button type="button" class="seq-toggle" :aria-expanded="String(seqVisible)" :aria-controls="fieldId('seq')" @click="seqOpen = !seqOpen">
				<i :class="['pi', seqVisible ? 'pi-chevron-down' : 'pi-chevron-right']" aria-hidden="true"></i>
				¿Ya facturabas desde este punto en otro sistema?
			</button>
			<div v-show="seqVisible" :id="fieldId('seq')" class="seq-body">
				<span class="field-label">Último secuencial usado, por tipo</span>
				<small class="field-help seq-help">Escribe el último número que usaste de cada tipo. Seguimos desde el siguiente para no repetir comprobantes ante el SRI. Si nunca emitiste, déjalo vacío.</small>
				<div class="formgrid grid">
					<div v-for="s in seqTypes" :key="s.key" class="field col-6 md:col-4">
						<label :for="fieldId('seq-' + s.key)" class="seq-label">{{ s.label }}</label>
						<InputText :id="fieldId('seq-' + s.key)" :modelValue="modelValue.seq[s.key]" @update:modelValue="setSeq(s.key, $event)"
							inputmode="numeric" maxlength="9" placeholder="0" :class="{'p-invalid': errors['seq_' + s.key]}" />
						<small v-if="errors['seq_' + s.key]" class="field-error" role="alert">{{ errors['seq_' + s.key] }}</small>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { ADDRESS_MAX_LENGTH, NAME_MAX_LENGTH, SEQ_TYPES, normalizeCode } from '../../service/establishments';

	export default {
		name: 'EmissionFields',
		props: {
			// {code, pointCode, name, address, seq: {receipt, …}}
			modelValue: { type: Object, required: true },
			errors: { type: Object, default: () => ({}) },
			mode: { type: String, default: 'establishment' },
			// Código del establecimiento al que va un punto nuevo (para mostrar la serie).
			establishmentCode: { type: String, default: '' },
			isMain: { type: Boolean, default: false },
			idPrefix: { type: String, required: true },
		},
		emits: ['update:modelValue'],
		data() {
			return {
				seqTypes: SEQ_TYPES,
				seqOpen: false,
				addressMaxLength: ADDRESS_MAX_LENGTH,
				nameMaxLength: NAME_MAX_LENGTH,
			}
		},
		computed: {
			// Un secuencial inválido abre el bloque aunque el usuario lo haya cerrado.
			seqVisible() {
				return this.seqOpen || Object.keys(this.errors).some((key) => key.startsWith('seq_'));
			},
			series() {
				const estab = this.mode === 'point' ? this.establishmentCode : normalizeCode(this.modelValue.code);
				return (estab || '___') + '-' + (normalizeCode(this.modelValue.pointCode) || '___');
			},
			namePlaceholder() {
				return this.isMain ? 'Matriz' : 'Ej.: Sucursal Norte';
			},
		},
		methods: {
			fieldId(name) {
				return this.idPrefix + '-' + name;
			},
			set(key, value) {
				this.$emit('update:modelValue', Object.assign({}, this.modelValue, {[key]: value}));
			},
			setSeq(key, value) {
				const seq = Object.assign({}, this.modelValue.seq, {[key]: value});
				this.$emit('update:modelValue', Object.assign({}, this.modelValue, {seq}));
			},
		},
	}
</script>

<style scoped>
	.field-label {
		display: block;
		font-weight: 500;
		margin-bottom: 0.5rem;
		color: var(--text-color);
	}
	.field-help,
	.field-error {
		display: block;
		margin-top: 0.375rem;
		font-size: 0.9rem;
		line-height: 1.4;
	}
	.field-help { color: var(--text-color-secondary); }
	.field-error { font-weight: 500; color: #B91C1C; }
	.code-input {
		max-width: 7rem;
		font-family: ui-monospace, Menlo, Consolas, monospace;
		letter-spacing: 0.08em;
	}
	.series {
		font-family: ui-monospace, Menlo, Consolas, monospace;
		font-weight: 600;
		color: var(--text-color);
	}
	.address-foot {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: 1rem;
	}
	.counter {
		margin-top: 0.375rem;
		flex-shrink: 0;
		font-size: 0.8rem;
		color: var(--text-color-secondary);
		font-variant-numeric: tabular-nums;
	}
	.seq-block {
		border-top: 1px solid var(--surface-border);
		padding-top: 0.75rem;
	}
	.seq-toggle {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		padding: 0;
		border: 0;
		background: none;
		color: var(--primary-color);
		font: inherit;
		font-weight: 500;
		cursor: pointer;
	}
	.seq-toggle:focus-visible {
		outline: 2px solid var(--primary-color);
		outline-offset: 2px;
	}
	.seq-body {
		margin-top: 0.75rem;
	}
	.seq-help {
		margin: -0.25rem 0 0.75rem;
	}
	.seq-label {
		display: block;
		font-size: 0.875rem;
		margin-bottom: 0.35rem;
		color: var(--text-color);
	}
</style>
